@if (isLoading) {
<div class="loader-overlay" role="status" aria-live="polite">
  <div class="heart-wrapper" aria-hidden="true">
    <svg class="heart-svg" viewBox="0 0 24 24">
      <path class="heart-shape" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5
                 2 6 4 4 6.5 4 8.24 4 9.91 5 10.54 6.36
                 11.17 5 12.76 4 14.5 4 17 4 19 6 19 8.5
                 c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
      <polyline class="ekg" points="3 12 7 12 9 8 11 15 13 6 15 12 21 12" fill="none" />
    </svg>
  </div>
  <p class="loader-text mt-3 fw-semibold text-dark">Loading reception portal...</p>
</div>
}

@if (!isLoading) {
<div class="reception-dashboard-container">
  <app-portal-navigation portalName="Reception Portal" portalIcon="bi-hospital-fill" collapseId="receptionMenu"
    [firstName]="userProfile?.firstName ?? ''" [lastName]="userProfile?.lastName ?? ''"
    [primaryItems]="primaryNavigationItems">
  </app-portal-navigation>

  <!-- MAIN CONTENT -->
  <div class="container-fluid p-3 p-md-4">
    <!-- Hero Banner -->
    <div class="booking-hero card border-0 mb-4">
      <div class="booking-hero__content">
        <div>
          <div class="booking-hero__eyebrow">Front Desk Operations</div>
          <h2 class="booking-hero__title mb-2">Receptionist Workspace</h2>
          <div class="booking-hero__meta">
            <span><i class="bi bi-person-circle me-1 text-primary"></i>{{ userProfile?.firstName }}
              {{ userProfile?.lastName }}</span>
            <span><i class="bi bi-clock me-1"></i>Active Shift</span>
            <span><i class="bi bi-people me-1 text-success"></i>{{ clinicDoctors.length }} Doctor{{
              clinicDoctors.length === 1 ? '' : 's'
              }}
              On Roster</span>
          </div>
        </div>

        <div class="booking-hero__status">
          <span class="status-pill" [ngClass]="clinicStatusDisplay.pillClass">
            <i class="bi me-1" [ngClass]="clinicStatusDisplay.icon"></i>{{ clinicStatusDisplay.label }}
          </span>
          @if (!isClinicActive) {
          <div class="booking-hero__status-note">
            Menu options are unavailable and cannot be selected until the practice status is
            active.
          </div>
          }
        </div>
      </div>
    </div>

    <!-- Alert: No Doctors Assigned -->
    @if (clinicDoctors.length === 0) {
    <div class="alert alert-warning d-flex align-items-start gap-3 mb-4" role="alert">
      <i class="bi bi-exclamation-triangle-fill fs-5 flex-shrink-0 mt-1"></i>
      <div>
        <strong>Not Linked to Any Doctor</strong>
        <p class="mb-0">
          You are currently not assigned to work with any doctor in this practice. Please
          contact your practice manager to be linked to a doctor before proceeding.
        </p>
      </div>
    </div>
    }

    <div class="row g-4">
      <!-- LEFT -->
      <div class="col-12 col-xl-4 d-flex flex-column gap-4">
        @if (clinicDoctors.length > 0) {
        <div class="card booking-card">
          <div class="card-header booking-card__header">
            <div>
              <div class="booking-card__eyebrow">Practitioner Filter</div>
              <h4 class="sub-title mb-0">Select Active Doctor</h4>
            </div>
          </div>

          <div class="card-body p-3">
            <label class="form-label">Viewing Schedule For:</label>
            <select class="form-select" [value]="defaultDoctorId"
              (change)="selectDoctor(clinicDoctors[$any($event.target).selectedIndex])">
              @for (doctor of clinicDoctors; track doctor.doctorId) {
              <option [value]="doctor.doctorId">
                Dr. {{ doctor.firstName }} {{ doctor.lastName }}
                @if (doctor.specialization) {
                &bull; {{ doctor.specialization }}
                }
              </option>
              }
            </select>
          </div>
        </div>
        }

        <personal-schedule (taskCreated)="onTaskCreated()"></personal-schedule>

        <!-- Upcoming Todo Tasks -->
        <div class="card booking-card">
          <div class="card-header booking-card__header">
            <div>
              <div class="booking-card__eyebrow">Future Tasks</div>
              <h4 class="sub-title mb-0">
                <i class="bi bi-list-check me-2" style="color: #15803d"></i>Upcoming Todos
              </h4>
            </div>
            @if (upcomingTodos.length > 0) {
            <span class="badge" style="background: #15803d; color: #fff">{{
              upcomingTodos.length
              }}</span>
            }
          </div>

          <div class="card-body p-3">
            @if (todosLoading) {
            <div class="text-center py-3">
              <div class="spinner-border spinner-border-sm" style="color: #15803d"></div>
              <div class="mt-2 text-muted small">Loading upcoming tasks...</div>
            </div>
            }

            @if (!todosLoading && upcomingTodos.length === 0) {
            <div class="empty-state empty-state--compact">
              <i class="bi bi-check2-all fs-2 d-block mb-1" style="color: #15803d"></i>
              <h6>All clear ahead!</h6>
              <p class="text-muted small mb-0">No upcoming tasks scheduled.</p>
            </div>
            }

            <div class="d-flex flex-column gap-2">
              @for (todo of paginatedTodos; track todo.id) {
              <div class="todo-item p-2 rounded-3 border" style="border-left: 3px solid #15803d !important">
                <div class="d-flex align-items-start justify-content-between gap-2">
                  <div class="flex-grow-1 min-w-0">
                    <div class="fw-semibold small">{{ todo.title }}</div>
                    @if (todo.description) {
                    <div class="text-muted" style="font-size: 0.72rem">
                      {{ todo.description }}
                    </div>
                    }
                    <div class="d-flex align-items-center gap-2 mt-1">
                      <span class="badge rounded-pill" [ngClass]="{
                              'bg-danger-subtle text-danger border border-danger-subtle':
                                todo.priority === 'HIGH',
                              'bg-warning-subtle text-warning border border-warning-subtle':
                                todo.priority === 'MEDIUM',
                              'bg-success-subtle text-success border border-success-subtle':
                                todo.priority === 'LOW',
                            }" style="font-size: 0.65rem">{{ todo.priority }}</span>
                      <small class="text-muted" style="font-size: 0.7rem">
                        <i class="bi bi-calendar-event me-1"></i>{{ todo.todoDate | date: 'dd MMM' }}
                      </small>
                    </div>
                  </div>
                  <div class="dropdown">
                    <button class="btn btn-sm btn-light border" type="button" data-bs-toggle="dropdown"
                      aria-expanded="false" title="Task actions">
                      <i class="bi bi-three-dots-vertical"></i>
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end shadow-sm">
                      <li>
                        <button class="dropdown-item text-danger" (click)="deleteUpcomingTodo(todo.id)">
                          <i class="bi bi-trash me-2"></i>Delete
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              }
            </div>

            <!-- Pagination Controls -->
            @if (todosPageSize > 1 && !todosLoading && upcomingTodos.length > 0) {
            <div class="d-flex align-items-center justify-content-between mt-3 pt-3 border-top">
              <small class="text-muted">
                Page {{ todosCurrentPage }} of {{ todosPageSize }}
              </small>

              <div class="btn-group btn-group-sm" role="group">
                <button type="button" class="btn btn-outline-secondary" [disabled]="todosCurrentPage === 1"
                  (click)="todosPreviousPage()">
                  <i class="bi bi-chevron-left"></i> Previous
                </button>
                <button type="button" class="btn btn-outline-secondary" [disabled]="todosCurrentPage === todosPageSize"
                  (click)="todosNextPage()">
                  Next <i class="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
            }
          </div>
        </div>
      </div>

      <!-- RIGHT -->
      <div class="col-12 col-xl-8 d-flex flex-column gap-3">
        <div class="card booking-card">
          <div class="card-header booking-card__header">
            <div>
              <div class="booking-card__eyebrow">Calendar & Availability</div>
              <h4 class="sub-title mb-0">
                <i class="bi bi-calendar3 me-2 text-primary"></i>Schedule Overview
              </h4>
            </div>
          </div>

          <div class="card-body p-0">
            @if (!!doctorId) {
            <schedule-calendar-view #calendar [appearanceSettings]="appearanceSettings"
              [doctorId]="doctorId"></schedule-calendar-view>
            }
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
}